import type { ComponentProps } from "react";
import { useCallback } from "react";
import type { TickProps } from "@ptsd/uikit";
import {
  Card,
  ChartContainer,
  ChartTooltip,
  getChartLines,
  getItemIndex,
  YAxisTick,
} from "@ptsd/uikit";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Circle, Text, View, XStack } from "tamagui";

import { api } from "~/utils/api";
import { dateToReadable } from "~/utils/date-to-readable";

//TODO: merge with diagnostic-scales-dynamics-linear-chart.tsx

interface LinearChartByScaleProps {
  scales: string[];
  researchId: string;
}

type CardProps = ComponentProps<typeof Card>;

export const LinearChartByScaleAndSurveyType: React.FC<
  LinearChartByScaleProps & CardProps
> = ({ researchId, scales, ...cardProps }) => {
  const [data] = api.phase.getScalesStats.useSuspenseQuery(
    {
      scales,
      researchId,
    },
    {
      select: (data) => {
        const config = Object.fromEntries(
          data.scales.map(({ key, color, name }) => [
            key,
            { label: name, color },
          ]),
        );

        return {
          config,
          chartData: data.chartData,
        };
      },
    },
  );

  const labelDateFormatter = useCallback(
    (value: string) => dateToReadable(value, true),
    [],
  );

  return (
    <Card
      data-id={"involvement-dynamics-chart-card"}
      paddingVertical={"$4.1"}
      shadow="md"
      gap={"$5"}
      {...cardProps}
    >
      <View position="relative" gap={"$4"}>
        <ChartContainer
          data-id={"involvement-dynamics-chart-container"}
          height={362}
          config={data?.config}
        >
          <LineChart
            data-id={"involvement-dynamics-chart"}
            accessibilityLayer
            data={data?.chartData}
            margin={{
              left: -10,
              right: 20,
              top: 12,
              bottom: 20,
            }}
          >
            <CartesianGrid
              data-id={"involvement-dynamics-chart-grid"}
              strokeDasharray={"3 3"}
              stroke="#CBCBCC"
              vertical={false}
            />
            <XAxis
              data-id={"involvement-dynamics-chart-x-axis"}
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={24}
              tick={{
                fill: "#636668",
                fontFamily: "Fixel Text",
                fontSize: 14,
                fontWeight: 400,
              }}
              tickFormatter={(v) => {
                const index = getItemIndex(data?.chartData, "date", `${v}`);
                return "Етап " + (index + 1);
              }}
            />
            <YAxis
              data-id={"involvement-dynamics-chart-y-axis"}
              tickLine={false}
              axisLine={false}
              tickMargin={16}
              tick={(props: TickProps) => <YAxisTick {...props} />}
            />
            <Tooltip
              data-id={"involvement-dynamics-chart-tooltip"}
              cursor={{
                stroke: "#CBCBCC",
                strokeWidth: 1,
                strokeDasharray: "3 3",
              }}
              content={
                <ChartTooltip.Content
                  data-id={"involvement-dynamics-chart-tooltip-content"}
                >
                  {(payload, _, date) => {
                    return (
                      <ChartTooltip.Container
                        data-id={"involvement-dynamics-chart-tooltip-container"}
                      >
                        <ChartTooltip.Title
                          data-id={"involvement-dynamics-chart-tooltip-title"}
                        >
                          {labelDateFormatter(date ?? "")}
                        </ChartTooltip.Title>
                        <View
                          gap={"$0.5"}
                          data-id={"involvement-dynamics-chart-tooltip-body"}
                        >
                          {payload?.map(({ color, value, config }, i) => (
                            <XStack
                              data-id={`involvement-dynamics-chart-tooltip-item-${i}`}
                              gap={"$0.5"}
                              alignItems="center"
                              key={i}
                            >
                              <Circle
                                backgroundColor={"white"}
                                borderWidth={"$0.3"}
                                borderColor={color}
                                size={"$2"}
                              />

                              <Text
                                data-id={`involvement-dynamics-chart-tooltip-text-${i}`}
                                fontFamily={"$body"}
                                fontSize={"$1"}
                                color={"black"}
                              >
                                {config?.label} {value}
                              </Text>
                            </XStack>
                          ))}
                        </View>
                      </ChartTooltip.Container>
                    );
                  }}
                </ChartTooltip.Content>
              }
            />
            {getChartLines(data?.config).map(({ dataKey, color }, i) => (
              <Line
                data-id={`involvement-dynamics-chart-line`}
                data-key={dataKey}
                key={i}
                dataKey={dataKey}
                type="linear"
                stroke={color}
                strokeWidth={2.5}
                dot={{
                  r: 8,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: "white",
                }}
                activeDot={{
                  r: 3,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: color,
                }}
              />
            ))}
          </LineChart>
        </ChartContainer>
      </View>
    </Card>
  );
};
